import { Component, Input } from '@angular/core';

/**
 * ContentSpinnerComponent
 *
 * Single Responsibility: Displays a configurable circular loading spinner
 * with alternating two-loader animation pattern for general-purpose loading
 * states including modals, pages, and content areas.
 *
 * Architecture:
 * - Two separate loaders that alternate visibility
 * - Loader 1: Primary base → Secondary fills clockwise (first half of cycle)
 * - Loader 2: Secondary base → Tertiary fills clockwise (second half of cycle)
 * - Loaders toggle visibility automatically based on animation timing
 *
 * Features:
 * - Configurable primary, secondary, and tertiary colors
 * - Configurable size (supports numbers and CSS units)
 * - Proportionally scaled border width
 * - Smooth continuous clockwise animation with alternating loaders
 * - 45-degree rotation for visual effect
 * - SSR compatible (pure CSS animations, no browser APIs)
 */
@Component({
    selector: 'app-content-spinner',
    templateUrl: './content-spinner.component.html',
    styleUrls: ['./content-spinner.component.scss'],
})
export class ContentSpinnerComponent {
    /**
     * Primary color for the base spinner border
     * Default: White (#FFFFFF)
     */
    @Input() primaryColor: string = '#F0F1F3';

    /**
     * Secondary color for the animated fill
     * Default: Orange (#FF3D00)
     */
    @Input() secondaryColor: string = '#00AFED';

    /**
     * Tertiary color for the animated fill
     * Default: Blue (#2196F3)
     */
    @Input() tertiaryColor: string = '#F0F1F3';

    /**
     * Size of the spinner
     * Accepts:
     * - Number (interpreted as pixels): 48, 64, 100
     * - String with CSS units: '48px', '3rem', '5em', '100%'
     * Default: 48 (48px)
     *
     * Note: Border width scales proportionally (base ratio: 10px border for 48px size)
     */
    @Input() size: number | string = 36;

    /**
     * Border thickness override (independent of size)
     * When provided, sets an explicit border width regardless of spinner size
     * When null/undefined, falls back to proportional calculation (size / 4.8)
     * Accepts:
     * - Number (interpreted as pixels): 5, 10, 15, 20
     * - String with CSS units: '5px', '0.5rem', '1em'
     * Default: undefined (uses proportional calculation)
     *
     * Use Cases:
     * - Fine-tuning visual weight without changing overall spinner size
     * - Matching specific design system requirements
     * - Creating thinner/thicker rings at the same radius
     * - Achieving pixel-perfect designs from Figma/mockups
     *
     * Example:
     * - size="36px" with borderThickness="2" → 36px spinner with 2px border
     * - size="36px" without borderThickness → 36px spinner with 7.5px border (proportional)
     */
    @Input() borderThickness?: number | string;

    /**
     * Animation duration in seconds
     * Controls the speed of the spinner animation
     * Lower values = faster animation, higher values = slower animation
     * Default: 3 (3 seconds per full cycle)
     *
     * Note: Each loader is visible for half this duration
     */
    @Input() duration: number = 2;

    /**
     * Base size used for calculating border width ratio
     * The default 48px spinner has a 10px border (ratio = 48/10 = 4.8)
     */
    private readonly BASE_SIZE = 48;
    private readonly BASE_BORDER_WIDTH = 10;
    private readonly BORDER_RATIO = this.BASE_SIZE / this.BASE_BORDER_WIDTH; // 4.8

    /**
     * Converts the size input to a CSS-compatible string value
     * Handles both number (pixels) and string (CSS units) inputs
     * SSR compatible: Pure string manipulation, no browser APIs
     *
     * @returns CSS size string (e.g., '48px', '3rem', '100%')
     */
    get sizeValue(): string {
        if (typeof this.size === 'number') {
            return `${this.size}px`;
        }
        return this.size;
    }

    /**
     * Calculates the border width with support for independent thickness control
     *
     * Priority Logic:
     * 1. If borderThickness is explicitly provided, use it (independent control)
     * 2. Otherwise, calculate proportionally from size (backward compatible behavior)
     *
     * This follows the Open/Closed Principle:
     * - Open for extension (new borderThickness parameter)
     * - Closed for modification (existing proportional behavior unchanged)
     *
     * SSR compatible: Pure mathematical calculation
     *
     * @returns Numeric border width value (in pixels)
     */
    get borderWidth(): number {
        // Strategy 1: Check if explicit thickness override is provided
        // This allows independent control of border thickness without affecting size
        if (this.borderThickness !== undefined && this.borderThickness !== null) {
            return this.parseBorderThickness();
        }

        // Strategy 2: Fall back to proportional calculation (existing behavior)
        // Maintains backward compatibility for existing component usage
        return this.calculateProportionalBorderWidth();
    }

    get borderWidthInPixels(): string {
        return `${this.borderWidth}px`;
    }

    /**
     * Calculates the animation duration for each individual loader
     * Each loader is visible for half the total cycle duration
     * SSR compatible: Pure mathematical calculation
     *
     * @returns CSS animation duration for a single loader (e.g., '1.5s' for 3s total)
     */
    get loaderDuration(): string {
        return `${this.duration / 2}s`;
    }

    /**
     * Converts full cycle duration to CSS-compatible animation-duration string
     * Used for the alternating visibility animation of both loaders
     * SSR compatible: Pure string manipulation
     *
     * @returns CSS animation duration (e.g., '3s', '2s')
     */
    get animationDuration(): string {
        return `${this.duration}s`;
    }

    /**
     * Parses the borderThickness input into a numeric pixel value
     * Handles both number and string inputs with CSS units
     *
     * Single Responsibility: Focused solely on parsing thickness input
     * SSR compatible: Pure string parsing, no browser APIs
     *
     * @returns Numeric border thickness value in pixels
     * @private
     */
    private parseBorderThickness(): number {
        // Handle numeric input (interpreted as pixels)
        if (typeof this.borderThickness === 'number') {
            return this.borderThickness;
        }

        // Handle string input with 'px' unit
        if (typeof this.borderThickness === 'string' && this.borderThickness.includes('px')) {
            return parseFloat(this.borderThickness);
        }

        // For non-pixel units (rem, em, %), use base border width as fallback
        // This maintains consistency with existing size handling logic
        // Note: In future, could enhance to support rem/em conversion
        return this.BASE_BORDER_WIDTH;
    }

    /**
     * Calculates proportional border width based on size
     * Extracted from original borderWidth getter for Single Responsibility
     * This method encapsulates the existing proportional scaling logic
     *
     * Single Responsibility: Focused solely on proportional calculation
     * SSR compatible: Pure mathematical calculation
     *
     * @returns Numeric border width based on size ratio (size / 4.8)
     * @private
     */
    private calculateProportionalBorderWidth(): number {
        let numericSize: number;

        // Extract numeric size value
        if (typeof this.size === 'number') {
            numericSize = this.size;
        }
        else if (typeof this.size === 'string' && this.size.includes('px')) {
            // Extract numeric value from pixel string (e.g., '48px' -> 48)
            numericSize = parseFloat(this.size);
        }
        else {
            // For non-pixel units (rem, em, %), use base border width
            // This maintains consistency when size is in relative units
            return this.BASE_BORDER_WIDTH;
        }

        // Calculate proportional border width using the defined ratio
        // Default: 48px size → 48 / 4.8 = 10px border
        return numericSize / this.BORDER_RATIO;
    }
}
